import React, { useCallback } from 'react'

import { FileText } from '@gravity-ui/icons'
import { EmptyState, type SortDescriptor } from '@heroui/react'
import { cn, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { ChevronUpIcon } from '@/components/icons'
import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useReportsStore } from '@/store/reports.store'
import { formatPLN } from '@/utils/currency'
import { SortDirection } from '@/utils/sorting'

import { ReportTableRow } from './ReportTableRow'


interface ReportsTableProps {
  reports: any[];
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onClone: (id: string) => void;
  onDelete: (id: string) => void;
}


function SortableColumnHeader({
                                children,
                                sortDirection,
                              }: {
  children: React.ReactNode;
  sortDirection?: 'ascending' | 'descending';
}) {
  return (
    <span className="flex items-center justify-between gap-1">
      {children}
      {!!sortDirection && (
        <ChevronUpIcon
          className={cn(
            'transform transition-transform duration-150 ease-out',
            sortDirection === 'descending' ? 'rotate-180' : '',
          )}
        />
      )}
    </span>
  )
}

export function ReportsTable({
                               reports,
                               onView,
                               onEdit,
                               onClone,
                               onDelete,
                             }: ReportsTableProps) {

  const { t } = useTranslation()
  const { pagination, setPagination, totalGross } = useReportsStore()

  const handlePageChange = useCallback((page: number) => {
    setPagination({ page: page  })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setPagination({
      sort: descriptor.column ? {
        key: descriptor.column as string,
        direction: descriptor.direction === 'ascending' ? SortDirection.ASC : SortDirection.DESC
      } : null,
      page: 0
    })
  }, [setPagination])

  const sortDescriptor = {
    column: pagination.sort?.key || '',
    direction: pagination.sort?.direction === SortDirection.ASC ? 'ascending' : 'descending'
  } as SortDescriptor

  const start = pagination.total === 0 ? 0 : pagination.page * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <>
      <Table className="min-h-[200px]">
        <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.72))]">
          <Table.Content
            aria-label="Reports list table"
            className="min-w-[1000px]"
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <Table.Header className="sticky top-0 z-10">
              <Table.Column allowsSorting id="identifier" isRowHeader>
                {({ sortDirection }) => (
                  <SortableColumnHeader
                    sortDirection={sortDirection}
                  >{t('reports.identifier', 'ID')}</SortableColumnHeader>
                )}
              </Table.Column>
              <Table.Column allowsSorting id="driverName" >
                {({ sortDirection }) => (
                  <SortableColumnHeader
                    sortDirection={sortDirection}
                  >{t('reports.driver')}</SortableColumnHeader>
                )}
              </Table.Column>
              <Table.Column allowsSorting id="vehiclePlate" >
                {({ sortDirection }) => (
                  <SortableColumnHeader
                    sortDirection={sortDirection}
                  >{t('reports.vehicle')}</SortableColumnHeader>
                )}
              </Table.Column>
              <Table.Column id="period" >{t('common.period')}</Table.Column>
              <Table.Column allowsSorting id="totalNetPLN" >
                {({ sortDirection }) => (
                  <SortableColumnHeader
                    sortDirection={sortDirection}
                  >{t('reports.table.pln_net_short', 'Сумма (чист) PLN')}</SortableColumnHeader>
                )}
              </Table.Column>
              <Table.Column allowsSorting id="totalPLN" >
                {({ sortDirection }) => (
                  <SortableColumnHeader
                    sortDirection={sortDirection}
                  >{t('reports.table.pln_amount')}</SortableColumnHeader>
                )}
              </Table.Column>
              <Table.Column className="text-end"></Table.Column>
            </Table.Header>
            <Table.Body
              renderEmptyState={() => (
                <EmptyState className="flex h-30 mt-6 w-full flex-col items-center justify-center gap-4 text-center">
                  <FileText className="size-6 text-zinc-400" />
                  <span className="text-sm text-zinc-500">{t('common.no_results', 'No results found')}</span>
                </EmptyState>
              )}
            >
              {reports.map(r => (
                <ReportTableRow
                  key={r.id}
                  report={r}
                  driverName={r.driverName || ''}
                  vehiclePlate={r.vehiclePlate || ''}
                  onView={onView}
                  onEdit={onEdit}
                  onClone={onClone}
                  onDelete={onDelete}
                />
              ))}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
        <Table.Footer>
          <div className="flex w-full items-center justify-between ">
            <div className="flex items-center gap-4">
              <RowsPerPage
                value={pagination.limit}
                onChange={handleLimitChange}
              />
              <span className="text-xs text-foreground/50">
                {t('common.pagination_summary', { start, end, total: pagination.total })}
              </span>
              <div className="h-4 w-px bg-foreground/15" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-foreground/50">{t('reports.total_gross_label', 'Итого')}:</span>
                <span className="text-xs font-bold tabular-nums text-foreground">{formatPLN(totalGross)} PLN</span>
              </div>
            </div>
            <Pagination
              page={pagination.page}
              total={pagination.totalPages}
              onChange={handlePageChange}
            />
          </div>
        </Table.Footer>
      </Table>
    </>
  )
}
